import { NotebookReferencePanelTabPanelProps } from "pages/notebooks/notebook-reference-panel/notebook-reference-panel";
import { UserBookmarkCountBy } from "models/enumerations/sorting/user-bookmarks/user-bookmark-count-by";
import EmptyMyLinkPanel from "organisms/my-link/empty-mylink-panel";
import Loader from "atoms/loaders/loader";
import MyBookmarksAccordionPanel from "organisms/my-link/my-bookmarks-dashboard/my-bookmarks-accordion-panel";
import PublicationAccordionPanelHeader from "organisms/my-link/my-bookmarks-dashboard/accordions/publication-accordion-panel-header";
import React from "react";
import useBookmarkGroups from "utilities/hooks/queries/my-link/bookmarks/grouped/use-bookmark-groups-query";
import UserBookmarkCountSortOption from "models/enumerations/sorting/user-bookmarks/user-bookmark-count-sort-option";
import UserBookmarkRecord from "models/view-models/user-bookmark-record";
import SectionRecord from "models/view-models/section-record";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization-utils";
import PublicationSectionService from "utilities/services/publications/sections/section-service";
import UserBookmarkType from "utilities/enumerations/user-bookmark-type";

interface ReferencesFromBookmarksProps
    extends NotebookReferencePanelTabPanelProps {
    onSectionClick: (
        section: SectionRecord,
        code: string,
        edition: string,
        parentNfpaLabel: string
    ) => void;
}

const ReferenceFromBookmarks = (props: ReferencesFromBookmarksProps) => {
    const { onSectionClick } = props;

    const { bookmarkGroups, isLoadingCounts } = useBookmarkGroups(
        UserBookmarkCountBy.Publication,
        { sortOption: UserBookmarkCountSortOption.BookOrder }
    );

    const handleBookmarkClick = async (bookmark: UserBookmarkRecord) => {
        try {
            const parentNfpaLabel =
                bookmark.annexNfpaLabel ||
                bookmark.articleNfpaLabel ||
                bookmark.chapterNfpaLabel ||
                "";
            const section = await fetchSection(
                bookmark.createdOnPublicationId!,
                bookmark.userBookmarkType === UserBookmarkType.Section
                    ? bookmark.externalId
                    : bookmark.parentExternalId
            );

            if (section) {
                onSectionClick(
                    section,
                    bookmark.publicationCode!,
                    bookmark.publicationEdition!,
                    parentNfpaLabel
                );
            } else {
                throw new Error();
            }
        } catch (err) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("loading"),
                    resource: t("bookmark").toLowerCase(),
                })
            );
        }
    };

    return (
        <>
            {isLoadingCounts ? (
                <Loader />
            ) : (
                <>
                    {bookmarkGroups?.length ? (
                        bookmarkGroups?.map((group) => (
                            <MyBookmarksAccordionPanel
                                onBookmarkClick={handleBookmarkClick}
                                gridColumn={1}
                                key={group.id}
                                id={group.id}
                                groupBy={UserBookmarkCountBy.Publication}
                                filterBookmarksBySearch={false}
                                header={
                                    <PublicationAccordionPanelHeader
                                        title={group.name}
                                        coverImageFileId={group.imageFileId}
                                        numberOfBookmarks={group.count}
                                    />
                                }
                            />
                        ))
                    ) : (
                        <EmptyMyLinkPanel />
                    )}
                </>
            )}
        </>
    );
};

const fetchSection = async (publicationId: number, externalId: string) => {
    try {
        const section = await PublicationSectionService.getByExternalId({
            externalId,
            publicationId,
        });

        return section.resultObject;
    } catch (err) {
        ToastManager.error(
            t("errors-actionResource", {
                action: t("loading"),
                resource: t("section").toLowerCase(),
            })
        );
    }
};

export default ReferenceFromBookmarks;
